// Social media SEO optimization utilities

export interface SocialMediaConfig {
  openGraph: {
    title: string;
    description: string;
    image: string;
    imageAlt: string;
    url: string;
    type: 'website' | 'article' | 'product' | 'profile';
    siteName: string;
    locale: string;
    author?: string;
    publishedTime?: string;
    modifiedTime?: string;
    section?: string;
    tags?: string[];
  };
  twitter: {
    card: 'summary' | 'summary_large_image' | 'app' | 'player';
    site: string;
    creator: string;
    title: string;
    description: string;
    image: string;
    imageAlt: string;
    app?: {
      name: string;
      id: {
        iphone: string;
        ipad: string;
        googleplay: string;
      };
      url: {
        iphone: string;
        ipad: string;
        googleplay: string;
      };
    };
  };
  facebook: {
    appId: string;
    admins?: string[];
    pages?: string[];
  };
  linkedin: {
    title: string;
    description: string;
    image: string;
    author: string;
  };
  pinterest: {
    description: string;
    image: string;
    nopin?: boolean;
  };
}

export interface SocialShareData {
  url: string;
  title: string;
  description: string;
  image: string;
  hashtags?: string[];
  via?: string;
}

export interface SocialAnalytics {
  platform: string;
  shares: number;
  likes: number;
  comments: number;
  clicks: number;
  reach: number;
  engagement: number;
}

/**
 * Generate Open Graph meta tags
 */
export const generateOpenGraphTags = (config: SocialMediaConfig['openGraph']): Record<string, string> => {
  const tags: Record<string, string> = {
    'og:title': config.title,
    'og:description': config.description,
    'og:image': config.image,
    'og:image:alt': config.imageAlt,
    'og:url': config.url,
    'og:type': config.type,
    'og:site_name': config.siteName,
    'og:locale': config.locale,
  };

  // Add optional tags
  if (config.author) tags['og:author'] = config.author;
  if (config.publishedTime) tags['article:published_time'] = config.publishedTime;
  if (config.modifiedTime) tags['article:modified_time'] = config.modifiedTime;
  if (config.section) tags['article:section'] = config.section;
  
  // Add tags for articles
  if (config.tags && config.tags.length > 0) {
    config.tags.forEach((tag, index) => {
      tags[`article:tag:${index}`] = tag;
    });
  }

  return tags;
};

/**
 * Generate Twitter Card meta tags
 */
export const generateTwitterCardTags = (config: SocialMediaConfig['twitter']): Record<string, string> => {
  const tags: Record<string, string> = {
    'twitter:card': config.card,
    'twitter:site': config.site,
    'twitter:creator': config.creator,
    'twitter:title': config.title,
    'twitter:description': config.description,
    'twitter:image': config.image,
    'twitter:image:alt': config.imageAlt,
  };

  // Add app-specific tags
  if (config.app) {
    tags['twitter:app:name:iphone'] = config.app.name;
    tags['twitter:app:name:ipad'] = config.app.name;
    tags['twitter:app:name:googleplay'] = config.app.name;
    
    tags['twitter:app:id:iphone'] = config.app.id.iphone;
    tags['twitter:app:id:ipad'] = config.app.id.ipad;
    tags['twitter:app:id:googleplay'] = config.app.id.googleplay;
    
    tags['twitter:app:url:iphone'] = config.app.url.iphone;
    tags['twitter:app:url:ipad'] = config.app.url.ipad;
    tags['twitter:app:url:googleplay'] = config.app.url.googleplay;
  }

  return tags;
};

/**
 * Generate Facebook-specific meta tags
 */
export const generateFacebookTags = (config: SocialMediaConfig['facebook']): Record<string, string> => {
  const tags: Record<string, string> = {
    'fb:app_id': config.appId,
  };

  if (config.admins && config.admins.length > 0) {
    tags['fb:admins'] = config.admins.join(',');
  }

  if (config.pages && config.pages.length > 0) {
    tags['fb:pages'] = config.pages.join(',');
  }

  return tags;
};

/**
 * Generate social sharing URLs
 */
export const generateSocialShareUrls = (data: SocialShareData): Record<string, string> => {
  const encodedUrl = encodeURIComponent(data.url);
  const encodedTitle = encodeURIComponent(data.title);
  const encodedDescription = encodeURIComponent(data.description);
  const encodedImage = encodeURIComponent(data.image);
  
  const hashtags = data.hashtags ? data.hashtags.map(tag => tag.replace('#', '')).join(',') : '';
  const via = data.via ? `&via=${encodeURIComponent(data.via)}` : '';

  return {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}${via}&hashtags=${hashtags}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    pinterest: `https://pinterest.com/pin/create/button/?url=${encodedUrl}&media=${encodedImage}&description=${encodedDescription}`,
    reddit: `https://reddit.com/submit?url=${encodedUrl}&title=${encodedTitle}`,
    whatsapp: `https://wa.me/?text=${encodedTitle}%20${encodedUrl}`,
    telegram: `https://t.me/share/url?url=${encodedUrl}&text=${encodedTitle}`,
    email: `mailto:?subject=${encodedTitle}&body=${encodedDescription}%0A%0A${encodedUrl}`,
  };
};

/**
 * Optimize images for social media platforms
 */
export const optimizeImageForSocial = (
  baseImageUrl: string,
  platform: 'facebook' | 'twitter' | 'linkedin' | 'pinterest' | 'instagram'
): {
  url: string;
  width: number;
  height: number;
  aspectRatio: string;
} => {
  const platformSpecs = {
    facebook: { width: 1200, height: 630, aspectRatio: '1.91:1' },
    twitter: { width: 1200, height: 675, aspectRatio: '16:9' },
    linkedin: { width: 1200, height: 627, aspectRatio: '1.91:1' },
    pinterest: { width: 1000, height: 1500, aspectRatio: '2:3' },
    instagram: { width: 1080, height: 1080, aspectRatio: '1:1' },
  };

  const spec = platformSpecs[platform];
  
  // In a real implementation, this would generate optimized images
  const optimizedUrl = baseImageUrl.replace(/\.(jpg|jpeg|png)$/i, `_${platform}_${spec.width}x${spec.height}.$1`);

  return {
    url: optimizedUrl,
    width: spec.width,
    height: spec.height,
    aspectRatio: spec.aspectRatio,
  };
};

/**
 * Generate social media structured data
 */
export const generateSocialStructuredData = (
  config: SocialMediaConfig,
  socialProfiles: string[]
): object => {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: config.openGraph.siteName,
    url: config.openGraph.url,
    logo: config.openGraph.image,
    sameAs: socialProfiles,
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      availableLanguage: ['English'],
    },
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'US',
    },
  };
};

/**
 * Track social media engagement
 */
export const trackSocialEngagement = (
  platform: string,
  action: 'share' | 'like' | 'comment' | 'click',
  url: string,
  title: string
): void => {
  // Google Analytics 4 tracking
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'social_engagement', {
      event_category: 'Social Media',
      event_label: `${platform}_${action}`,
      custom_parameters: {
        social_platform: platform,
        social_action: action,
        page_url: url,
        page_title: title,
      },
    });
  }

  // Facebook Pixel tracking
  if (typeof window !== 'undefined' && (window as any).fbq) {
    (window as any).fbq('track', 'Share', {
      content_name: title,
      content_url: url,
    });
  }

  // Custom analytics
  console.log(`Social engagement: ${platform} ${action} on ${title}`);
};

/**
 * Validate social media meta tags
 */
export const validateSocialMetaTags = (document: Document): {
  score: number;
  issues: string[];
  recommendations: string[];
} => {
  const issues: string[] = [];
  const recommendations: string[] = [];
  let score = 100;

  // Check Open Graph tags
  const requiredOGTags = ['og:title', 'og:description', 'og:image', 'og:url', 'og:type'];
  requiredOGTags.forEach(tag => {
    const element = document.querySelector(`meta[property="${tag}"]`);
    if (!element) {
      issues.push(`Missing ${tag} meta tag`);
      score -= 15;
    }
  });

  // Check Twitter Card tags
  const requiredTwitterTags = ['twitter:card', 'twitter:title', 'twitter:description', 'twitter:image'];
  requiredTwitterTags.forEach(tag => {
    const element = document.querySelector(`meta[name="${tag}"]`);
    if (!element) {
      issues.push(`Missing ${tag} meta tag`);
      score -= 10;
    }
  });

  // Check image dimensions
  const ogImage = document.querySelector('meta[property="og:image"]');
  if (ogImage) {
    const imageUrl = ogImage.getAttribute('content');
    if (imageUrl && !imageUrl.includes('1200x630')) {
      recommendations.push('Optimize Open Graph image to 1200x630 pixels for best results');
    }
  }

  // Check description length
  const ogDescription = document.querySelector('meta[property="og:description"]');
  if (ogDescription) {
    const description = ogDescription.getAttribute('content') || '';
    if (description.length > 300) {
      recommendations.push('Open Graph description should be under 300 characters');
    }
    if (description.length < 50) {
      recommendations.push('Open Graph description should be at least 50 characters');
    }
  }

  // Check title length
  const ogTitle = document.querySelector('meta[property="og:title"]');
  if (ogTitle) {
    const title = ogTitle.getAttribute('content') || '';
    if (title.length > 60) {
      recommendations.push('Open Graph title should be under 60 characters');
    }
  }

  return { score: Math.max(0, score), issues, recommendations };
};

/**
 * Generate social media content calendar suggestions
 */
export const generateSocialContentSuggestions = (
  keywords: string[],
  industry: string,
  targetAudience: string
): Array<{
  platform: string;
  contentType: string;
  title: string;
  description: string;
  hashtags: string[];
  bestTime: string;
}> => {
  const suggestions = [];

  // Platform-specific content types
  const contentTypes = {
    facebook: ['article', 'video', 'infographic', 'poll'],
    twitter: ['thread', 'tip', 'news', 'quote'],
    linkedin: ['article', 'industry-insight', 'case-study', 'tip'],
    instagram: ['carousel', 'story', 'reel', 'infographic'],
    pinterest: ['infographic', 'guide', 'tutorial', 'inspiration'],
  };

  // Generate suggestions for each platform
  Object.entries(contentTypes).forEach(([platform, types]) => {
    types.forEach(type => {
      keywords.slice(0, 3).forEach(keyword => {
        suggestions.push({
          platform,
          contentType: type,
          title: `${type === 'article' ? 'The Ultimate Guide to' : 'How to Master'} ${keyword}`,
          description: `Share insights about ${keyword} for ${targetAudience} in the ${industry} industry`,
          hashtags: [
            `#${keyword.replace(/\s+/g, '')}`,
            `#${industry}`,
            `#${targetAudience.replace(/\s+/g, '')}`,
            '#SEO',
            '#DigitalMarketing',
          ],
          bestTime: getBestPostingTime(platform),
        });
      });
    });
  });

  return suggestions.slice(0, 20); // Limit to 20 suggestions
};

/**
 * Get optimal posting times for social platforms
 */
const getBestPostingTime = (platform: string): string => {
  const times = {
    facebook: 'Tuesday-Thursday, 9 AM - 1 PM',
    twitter: 'Tuesday-Thursday, 9 AM - 3 PM',
    linkedin: 'Tuesday-Wednesday, 8 AM - 10 AM',
    instagram: 'Monday-Friday, 11 AM - 1 PM',
    pinterest: 'Saturday-Monday, 8 PM - 11 PM',
  };

  return times[platform as keyof typeof times] || 'Weekdays, 9 AM - 5 PM';
};

/**
 * Analyze competitor social media presence
 */
export const analyzeCompetitorSocial = (
  competitors: Array<{
    name: string;
    website: string;
    socialProfiles: Record<string, string>;
  }>
): {
  insights: string[];
  opportunities: string[];
  benchmarks: Record<string, number>;
} => {
  const insights: string[] = [];
  const opportunities: string[] = [];
  const benchmarks: Record<string, number> = {};

  // Analyze platform presence
  const platformCounts: Record<string, number> = {};
  competitors.forEach(competitor => {
    Object.keys(competitor.socialProfiles).forEach(platform => {
      platformCounts[platform] = (platformCounts[platform] || 0) + 1;
    });
  });

  // Generate insights
  const totalCompetitors = competitors.length;
  Object.entries(platformCounts).forEach(([platform, count]) => {
    const percentage = (count / totalCompetitors) * 100;
    benchmarks[platform] = percentage;
    
    if (percentage > 80) {
      insights.push(`${percentage.toFixed(0)}% of competitors are active on ${platform}`);
    }
    
    if (percentage < 50) {
      opportunities.push(`Only ${percentage.toFixed(0)}% of competitors use ${platform} - potential opportunity`);
    }
  });

  return { insights, opportunities, benchmarks };
};

/**
 * Generate social proof elements
 */
export const generateSocialProofElements = (
  socialData: {
    followers: Record<string, number>;
    testimonials: Array<{
      text: string;
      author: string;
      platform: string;
    }>;
    mentions: number;
    shares: number;
  }
): {
  elements: Array<{
    type: 'follower-count' | 'testimonial' | 'social-mention' | 'share-count';
    content: string;
    platform?: string;
  }>;
} => {
  const elements = [];

  // Follower counts
  Object.entries(socialData.followers).forEach(([platform, count]) => {
    if (count > 1000) {
      elements.push({
        type: 'follower-count' as const,
        content: `${count.toLocaleString()} ${platform} followers`,
        platform,
      });
    }
  });

  // Testimonials
  socialData.testimonials.forEach(testimonial => {
    elements.push({
      type: 'testimonial' as const,
      content: `"${testimonial.text}" - ${testimonial.author}`,
      platform: testimonial.platform,
    });
  });

  // Social mentions
  if (socialData.mentions > 100) {
    elements.push({
      type: 'social-mention' as const,
      content: `${socialData.mentions.toLocaleString()} social media mentions`,
    });
  }

  // Share counts
  if (socialData.shares > 500) {
    elements.push({
      type: 'share-count' as const,
      content: `${socialData.shares.toLocaleString()} content shares`,
    });
  }

  return { elements };
};